import React, { Component } from "react"
import trans from "trans"

import {
    css,
    Grid,
    GridCell,
    Theme,
    Message,
    Icon,
    Button,
    Toggler,
    Spinner,
    Badge,
    Paginator,
    InputGroup,
} from "@bdfx/mergado-ui-kit"

 import { Icon as IconifyIcon }  from "@iconify-icon/react";

import ShapeType from "shape-entities/types/shape"
import { getExternalImageCollections, getExternalImageCollection, saveImageAsCustomSetFile, searchExternalImage } from "app/helpers/api"
import { AxiosResponse } from "axios"
import ChangeSvgColors from "./change-svg-colors"
import { SizeType } from "app/types/model-types"
import { FormNameType } from '../../app/types/model-types';
import TransparentColor from "./transparent-color";
import { iconDisabledColor } from "template-edit/components/tools-panel";

const { min, max, round, ceil } = Math

const PREVIEW_WIDTH = 28

type ResultsType = {
    icons: string[]
    total: number
    limit: number
    start: number
    collections: IconifyCollection[]
}

type IconifySearchResponse = {
    search: string
    limit: number
    results: ResultsType
    request: {
        query: string,
        limit: string
        start: string
    }
}

type SelectedCollectionType = {
    prefix: string
    total: number
    title: string
    categories: object[]  //"categories":{"Actions":["3d-rotation","3d-rotation-outline-rounded","3d-rotation-rounded","3d-rotation-
    suffixes: object[]    //"suffixes":{"":"Regular","rounded":"Rounded","sharp":"Sharp","outline":"Outline","outline-rounded":"Outline Rounded","outline-sharp":"Outline Sharp"}} */
}

type IconifyCollection = {
    name: string
    author: {
        name: string
        url: string
    }
    license: {
        title: string
        spdx: string
        url: string
    }
    total: number
    version: string
    samples: string[]
    height: number|number[]
    displayHeight: number
    category: string
    tags: string
    palette: boolean
}

interface Props {
    formName: FormNameType
    change: Function
    shape: ShapeType
    templateSize: SizeType
    search: string
    isSearching: boolean
    setSearching: (isSearching: boolean) => void
}

interface State {
    isLoadingCollections: boolean
    limit: number
    start: number
    color: string
    results: ResultsType
    collections: IconifyCollection[]
    prefix: string
    selectedCollection: SelectedCollectionType
    selectedCategory: string
    categoryLimit: number
    categoryPage: number
    searchedFor: string
}

const PAGE_SIZE = 100
const SEARCH_DEBOUNCE_MS = 1000

class IconifyForm extends Component<Props, State> {
    searchDebounceTimeout: ReturnType<typeof setTimeout> = null

    constructor(props) {
        super(props)

        this.state = {
            start: 1,
            limit: PAGE_SIZE,
            color: Theme.steelblue,
            results: null,
            collections: null,
            isLoadingCollections: null,
            prefix: null,
            selectedCollection: null,
            selectedCategory: null,
            categoryLimit: 60,
            categoryPage: 1,
            searchedFor: null,
        }
    }

    componentDidMount(): void {
        const search = this.props.search ? this.props.search : (this.props.shape.external_image_prompt ? this.props.shape.external_image_prompt : null)
        if (search) {
            this.searchImage(search, PAGE_SIZE, 1)
        }

        this.getCollections()
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        const { search } = this.props

        if (search !== prevProps.search) {
            if (this.searchDebounceTimeout) {
                clearTimeout(this.searchDebounceTimeout)
                this.searchDebounceTimeout = null
            }

            if (search && search !== this.state.searchedFor) {
                this.searchDebounceTimeout = setTimeout(() => {
                    this.searchDebounceTimeout = null
                    if (this.props.search === search && search !== this.state.searchedFor) {
                        this.searchImage(search, PAGE_SIZE, 1)
                    }
                }, SEARCH_DEBOUNCE_MS)
            }
        }
    }

    componentWillUnmount(): void {
        if (this.searchDebounceTimeout) {
            clearTimeout(this.searchDebounceTimeout)
        }
    }

    saveImageToCustomSets = (icon: string, color: string, origFileName: string) => {
        const { shape, templateSize, formName } = this.props

        const minSide = min(templateSize.width, templateSize.height, 400)
        const side = ceil(minSide / 2)

        saveImageAsCustomSetFile(icon, shape, side, side, color, origFileName)
            .then((response: AxiosResponse<ShapeType>) => {
                this.props.change(`${formName}.shapes`, shape.index, response.data)
                console.debug(`updated shape ${shape.index} in response is`, response.data)
            })
            .catch((e) => console.error('cannot save image as a custom set file', e))
    }

    getCollections = () => {
        this.setState({ isLoadingCollections: true })
        getExternalImageCollections()
            .then((response: AxiosResponse<IconifyCollection[]>) => {
                this.setState({ collections: response.data })
            })
            .catch((e) => {
                console.error('cannot search external source', e)
            })
            .finally(() => this.setState({ isLoadingCollections: false }))
    }

    getCollectionByPrefix = (prefix: string) => {
        this.setState({
            isLoadingCollections: true,
            prefix: prefix
        })

        getExternalImageCollection(prefix)
            .then((response: AxiosResponse<SelectedCollectionType>) => {
                console.debug(`we have selected collection`, response.data)
                this.setState({ selectedCollection: response.data })
            })
            .catch((e) => {
                console.error('cannot search external source', e)
            })
            .finally(() => this.setState({ isLoadingCollections: false }))
    }

    searchImage = (search: string, limit: number, start: number) => {    //prev
        const { shape, formName } = this.props

        this.setState({
            start: start,
            limit: limit,
            searchedFor: search,
        })

        this.props.setSearching(true)
        this.props.change(`${formName}.shapes.${shape.index}`, 'external_image_prompt', search)

        searchExternalImage(search, limit, start)
            .then((response: AxiosResponse<IconifySearchResponse>) => {
                this.setState({ results: response.data.results })
            })
            .catch((e) => {
                console.error('cannot search external source', e)
            })
            .finally(() => this.props.setSearching(false))
    }

    render() {
        const { shape, search, isSearching } = this.props

        const {
            limit, results, collections, prefix, selectedCollection, selectedCategory, categoryPage,
            categoryLimit, isLoadingCollections, start, color
        } = this.state

        const selectedCategoryObject = selectedCategory && selectedCollection?.categories && typeof selectedCollection.categories[selectedCategory] !== 'undefined' && Array.isArray(selectedCollection.categories[selectedCategory]) ?
            selectedCollection.categories[selectedCategory] : null

        const categoryIconsPaginator = selectedCategoryObject && selectedCategoryObject.length > categoryLimit ?
            <Paginator
                showFirstAndLast={true}
                showPrevAndNext={true}
                maxLinks={3}
                labelFirst={`««`}
                labelPrevious={`«`}
                labelNext={`»`}
                labelLast={`»»`}
                currentPage={categoryPage}
                lastPage={ceil(selectedCategoryObject.length / categoryLimit)}
                onPageChange={(page) => this.setState({ categoryPage: page })}
                style={{
                    margin: '10px 0px',
                    gridGap: '2px',
                }}
            /> : <span />

        return (
            <StyledFormGrid cols={"auto"} gap={'15px'}>
                <GridCell>
                    {(!prefix && results && results.icons && results.icons.length) ?
                        <React.Fragment>
                            <PaginatorGrid>
                                <GridCell>
                                    {start > PAGE_SIZE &&
                                        <Button
                                            type="void"
                                            color={'nocolor'}
                                            title={trans('previous')}
                                            onClick={() => this.searchImage(search, limit - PAGE_SIZE, start - PAGE_SIZE)}
                                            icon={<Icon type={'chevron-left'} color={Theme.steelblue} />}
                                        />
                                    }
                                </GridCell>
                                <GridCell>
                                    {results.total >= results.limit &&
                                        <Button
                                            type="void"
                                            color={'nocolor'}
                                            title={trans('next')}
                                            onClick={() => this.searchImage(search, limit + PAGE_SIZE, start + PAGE_SIZE)}
                                            icon={<Icon type={'chevron-right'} color={Theme.steelblue} />}
                                        />
                                    }
                                </GridCell>
                            </PaginatorGrid>


                            <StyledIconGrid>
                                {results.icons.map((icon: string, i: number) => {
                                    const a = icon.split(':')
                                    const c = results.collections[a[0]] || null
                                    const origFileName = icon   //????? name of file

                                    return <GridCell className="clickable" key={`image_${i}`} onClick={() => this.saveImageToCustomSets(icon, color, origFileName)} title={c.name}>
                                        <IconifyIcon icon={icon} width={PREVIEW_WIDTH} color={color} />
                                        {/* {c &&
                                            <div>
                                                <span style={{ color: Theme.steelblue }}>{c.name}</span><br />
                                                {c.author &&
                                                    <a href={c.author.url}><small style={{ color: iconDisabledColor }}>{c.author.name}</small></a>
                                                }
                                            </div>
                                        } */}
                                    </GridCell>
                                })}
                            </StyledIconGrid>
                        </React.Fragment> :
                        !!(search && !isSearching && !prefix) ? <Message type={"info"} closeable={true} icon={<Icon type={'info'} color={Theme.blue} size={28} />}>{trans("no_search_image_results")}</Message> : null
                    }
                </GridCell>

                {shape.imageBlob &&
                    <React.Fragment>
                        <GridCell style={{ textAlign: 'center', alignSelf: 'center' }}>
                            <img src={shape.imageBlob} width={'200'} height={'200'} />
                        </GridCell>
                        <GridCell style={{ textAlign: 'center', alignSelf: 'center' }}>
                            <ChangeSvgColors shape={shape} />
                        </GridCell>
                    </React.Fragment>
                }

                <GridCell>
                    <Grid cols={'repeat(2, 1fr)'} gap={'10px'}>
                        <GridCell style={{ alignSelf: 'center' }} >{trans('icon_color')}</GridCell>
                        <GridCell><TransparentColor value={color} changeValue={(color: string) => this.setState({ color })} /></GridCell>
                    </Grid>
                </GridCell>
                <GridCell>

                    {(prefix && selectedCollection) &&
                        <GridCell>
                            <StyledNavigationGrid>
                                <GridCell style={{ cursor: 'pointer' }} onClick={() => {
                                    this.setState({
                                        isLoadingCollections: false,
                                        prefix: null,
                                    })
                                }}>{trans('icons')}</GridCell>
                                <GridCell> › {selectedCollection.title} <small>({selectedCollection.total})</small></GridCell>
                            </StyledNavigationGrid>

                            <StyledCategories>
                                {Object.keys(selectedCollection.categories || {}).map((cat, i) => <Badge
                                    key={`cat_${cat}_${i}`}
                                    className={'clickable'}
                                    small={true}
                                    secondary={selectedCategory !== cat}
                                    onClick={() => {
                                        this.setState({
                                            selectedCategory: cat,
                                            categoryPage: categoryPage > 1 ? 1 : categoryPage,
                                        })
                                    }}>{cat}</Badge>
                                )}
                            </StyledCategories>

                            {selectedCategoryObject &&
                                <React.Fragment>
                                    {categoryIconsPaginator}
                                    <StyledIconGrid>
                                        {selectedCategoryObject
                                            .slice((categoryPage - 1) * categoryLimit, (categoryPage) * categoryLimit)
                                            .map((icon) => <GridCell
                                                title={`${selectedCollection.prefix}:${icon}`}
                                                key={`icon_${icon}`}
                                                onClick={() => this.saveImageToCustomSets(`${selectedCollection.prefix}:${icon}`, color, icon)}>
                                                <IconifyIcon icon={`${selectedCollection.prefix}:${icon}`} width={PREVIEW_WIDTH} color={color} />
                                            </GridCell>
                                            )
                                        }
                                    </StyledIconGrid>
                                </React.Fragment>
                            }
                        </GridCell>
                    }

                    {isLoadingCollections ?
                        <Spinner type="bubbles" /> : (collections && !prefix) ?
                            <Grid cols={'repeat(2, 1fr)'} gap={'10px'}>
                                {Object.keys(collections).map(prefix => {
                                    const collection = collections[prefix]
                                    return <GridCell
                                        key={`collection_${prefix}`}
                                        onClick={() => this.getCollectionByPrefix(prefix)}
                                        style={{
                                            background: 'aliceblue',
                                            borderRadius: '4px',
                                            padding: '10px',
                                            position: 'relative',
                                            cursor: 'pointer',
                                        }}>
                                        <Grid cols={'1fr 1fr'}>
                                            <GridCell style={{ fontSize: '10px' }}>
                                                {collection.name}<br />
                                                <a href={collection.license.url}>{collection.license.spdx}</a><br />
                                                {trans('total_icons')}: {collection.total}
                                            </GridCell>
                                            <GridCell>
                                                <Grid cols={'repeat(3, 1fr)'}>
                                                    {collection.samples.map((sample, j) => <GridCell className={'clickable'} key={`sample_${prefix}_${j}`} title={`${prefix}:${sample}`} onClick={() => this.saveImageToCustomSets(`${prefix}:${sample}`, color, sample)}>
                                                        <IconifyIcon icon={`${prefix}:${sample}`} width={PREVIEW_WIDTH} color={color} />
                                                    </GridCell>)}
                                                </Grid>
                                            </GridCell>
                                        </Grid>

                                        <Badge
                                            color={Theme.blue}
                                            small={true}
                                            style={{
                                                position: 'absolute',
                                                bottom: '-5px',
                                                right: '-3px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => this.getCollectionByPrefix(prefix)}
                                        >{trans('more')}</Badge>
                                    </GridCell>
                                })}
                            </Grid> :
                            null
                    }
                </GridCell>
            </StyledFormGrid>
        )
    }
}

const StyledFormGrid = css(Grid)({})

const PaginatorGrid = css(Grid)({
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridGap: '20px',
    marginBottom: '20px',
    ' .muk-gridcell': {
        alignSelf: 'center',
        textAlign: 'center',
        color: Theme.steelblue,
        fontSize: '20px',
        fontWeight: 'bold',
    }
})

const StyledCategories = css('div')({

})

const StyledNavigationGrid = css(Grid)({
    marginTop: '40px',
    marginBottom: '20px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(40px, max-content))',
    gridAutoFlow: 'row !important',
    gridGap: '10px',
    ' .muk-gridcell': {
        textAlign: 'center',
        alignSelf: 'center',
        color: iconDisabledColor,
    },
})

const StyledIconGrid = css(Grid)({
    gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
    gridAutoFlow: 'row',
    ' .muk-gridcell': {
        textAlign: 'center',
        alignSelf: 'center',
    },
})

export default IconifyForm